import React, { useState } from 'react';
import { Formik, Form } from 'formik';
import PropTypes from 'prop-types';

import { createInitialValues, createFormValidationFn } from 'client/helpers/formikHelpers';
import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';
import SrButton, { SR_BUTTON_THEME_ADOBE_GREEN } from 'client/components/SrButton/SrButton';
import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import {
  normalizeCoordinateInput,
  normalizeStateCodeInput,
  setNormalizedFormikValue,
} from 'client/helpers/formInputNormalization';

/**
 * Get form fields based on current mode
 * @param {boolean} isManualCreationForm - false = autocomplete, true = manual
 * @param {Function} setIsManualCreationForm - State setter for form mode
 * @param {boolean} disabled - Whether the form is currently submitting
 */
const getFormFields = ({
  isManualCreationForm,
  setIsManualCreationForm,
  disabled,
  formikBag,
}) => {
  if (!isManualCreationForm) {
    return [
      {
        id: 'addressString',
        title: 'New address *',
        titleToggle: {
          text: "Can't find address?",
          onClick: disabled ? undefined : () => setIsManualCreationForm(true),
        },
        type: 'smarty_address_autocomplete',
        properties: {},
        validations: ['required'],
      },
    ];
  }

  return [
    {
      id: 'streetNumber',
      title: 'Street Number *',
      titleToggle: {
        text: 'Use address search?',
        onClick: disabled ? undefined : () => setIsManualCreationForm(false),
      },
      type: 'short_text',
      properties: {
        placeholder: 'e.g., 123',
        onlyAcceptNumbers: true,
      },
      validations: ['required'],
    },
    {
      id: 'streetName',
      title: 'Street Name *',
      type: 'short_text',
      properties: {
        placeholder: 'e.g., Main',
      },
      validations: ['required'],
    },
    {
      id: 'streetPrefix',
      title: 'Street Prefix',
      type: 'short_text',
      properties: {
        placeholder: 'e.g., N',
      },
    },
    {
      id: 'streetSuffix',
      title: 'Street Suffix',
      type: 'short_text',
      properties: {
        placeholder: 'e.g., St',
      },
    },
    {
      id: 'unitNumber',
      title: 'Unit Number',
      type: 'short_text',
      properties: {
        placeholder: 'e.g., Apt 4B',
      },
    },
    {
      id: 'city',
      title: 'City *',
      type: 'short_text',
      properties: {
        placeholder: 'e.g., Raleigh',
      },
      validations: ['required'],
    },
    {
      id: 'stateCode',
      title: 'State *',
      type: 'short_text',
      tooltipCopy: 'Use 2-letter state code (e.g., NC).',
      properties: {
        placeholder: 'e.g., NC',
        onChange: (event) => {
          setNormalizedFormikValue({
            formikBag,
            fieldId: 'stateCode',
            value: event.target.value || '',
            normalizer: normalizeStateCodeInput,
          });
        },
      },
      validations: ['required', 'stateCode'],
    },
    {
      id: 'zip',
      title: 'Zip Code *',
      type: 'zipCode',
      properties: {
        placeholder: 'e.g., 27601',
      },
      validations: ['required', 'zipCode'],
    },
    {
      id: 'county',
      title: 'County',
      type: 'short_text',
      properties: {
        placeholder: 'e.g., Wake',
      },
    },
    {
      id: 'latitude',
      title: 'Latitude *',
      type: 'short_text',
      properties: {
        type: 'number',
        step: 'any',
        placeholder: 'e.g., 35.7796',
        onChange: (event) => {
          setNormalizedFormikValue({
            formikBag,
            fieldId: 'latitude',
            value: event.target.value || '',
            normalizer: normalizeCoordinateInput,
          });
        },
      },
      validations: ['required', 'latitude'],
    },
    {
      id: 'longitude',
      title: 'Longitude *',
      type: 'short_text',
      properties: {
        type: 'number',
        step: 'any',
        placeholder: 'e.g., -78.6382',
        onChange: (event) => {
          setNormalizedFormikValue({
            formikBag,
            fieldId: 'longitude',
            value: event.target.value || '',
            normalizer: normalizeCoordinateInput,
          });
        },
      },
      validations: ['required', 'longitude'],
    },
  ];
};

export default function UPAddressResolutionForm({
  disabled,
  onRegister,
}) {
  // Form mode state: false = autocomplete, true = manual
  const [isManualCreationForm, setIsManualCreationForm] = useState(false);

  // Initial form values
  const initialValues = createInitialValues({
    formFields: getFormFields({ isManualCreationForm, setIsManualCreationForm, disabled }),
    defaultValues: {
      addressString: '',
      streetNumber: '',
      streetName: '',
      streetPrefix: '',
      streetSuffix: '',
      city: '',
      stateCode: '',
      zip: '',
      county: '',
      unitNumber: '',
      latitude: '',
      longitude: '',
    },
  });

  // Handle form submission
  const handleSubmit = (formValues, formikBag) => {
    if (!isManualCreationForm) {
      onRegister({
        addressString: formValues.addressString,
        onSuccess: () => formikBag.resetForm(),
      });
      return;
    }

    onRegister({
      addressFields: {
        streetNumber: formValues.streetNumber,
        streetName: formValues.streetName,
        streetPrefix: formValues.streetPrefix,
        streetSuffix: formValues.streetSuffix,
        unitNumber: formValues.unitNumber,
        city: formValues.city,
        stateCode: formValues.stateCode ? formValues.stateCode.toUpperCase() : formValues.stateCode,
        zip: formValues.zip,
        county: formValues.county,
        latitude: formValues.latitude,
        longitude: formValues.longitude,
        countryCode: 'US',
      },
      onSuccess: () => formikBag.resetForm(),
    });
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validate={createFormValidationFn(getFormFields({ isManualCreationForm, setIsManualCreationForm, disabled }))}
      onSubmit={handleSubmit}
      render={(formikBag) => {
        const formFields = getFormFields({
          isManualCreationForm,
          setIsManualCreationForm,
          disabled,
          formikBag,
        });

        const shouldShowLinkButton =
          (isManualCreationForm
            ? formikBag.isValid
            : Boolean(formikBag.values.addressString) && formikBag.isValid
          ) || disabled;

        return (
          <Form>
            {/* Dynamic form fields */}
            {formFields.map((formField) => (
              <SrBox key={formField.id} marginBottom="16px">
                <SrFormikField
                  formField={formField}
                  formikBag={formikBag}
                  theme={SR_FIELD_THEME_ADOBE}
                  disabled={disabled}
                />
              </SrBox>
            ))}

            {/* Action buttons */}
            <SrBox paddingTop="12px" display={shouldShowLinkButton ? 'block' : 'none'}>
              <SrFlex direction="col" gap="16px">
                <SrButton
                  theme={SR_BUTTON_THEME_ADOBE_GREEN}
                  disabled={!formikBag.isValid || disabled}
                  type="submit"
                >
                  {disabled ? 'Linking...' : 'Link Address'}
                </SrButton>
              </SrFlex>
            </SrBox>
          </Form>
        );
      }}
    />
  );
}

UPAddressResolutionForm.propTypes = {
  disabled: PropTypes.bool,
  onRegister: PropTypes.func,
};

UPAddressResolutionForm.defaultProps = {
  disabled: false,
  onRegister: () => { },
};
